import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Navbar from './Navbar';

const Welcome = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white font-sans overflow-hidden">
      <Navbar currentPage="Home" />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(239,68,68,0.15),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(147,51,234,0.1),transparent_50%)]"></div>
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0 bg-[linear-gradient(rgba(239,68,68,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(239,68,68,0.1)_1px,transparent_1px)] bg-[length:60px_60px]"></div>
          </div>

          {/* Animated Orbs */}
          <div className="absolute top-20 left-20 w-72 h-72 bg-red-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-2 sm:py-3 bg-white/5 rounded-full border border-white/10 mb-6 sm:mb-8 backdrop-blur-sm">
            <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
            <span className="text-white/80 text-xs sm:text-sm font-medium tracking-wider">COMICS</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-black mb-6 sm:mb-8 tracking-tight">
            <span className="bg-gradient-to-r from-red-500 via-red-400 to-purple-500 bg-clip-text text-transparent animate-gradient">
              COMICS
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-xl md:text-2xl lg:text-3xl text-red-400/80 mb-4 sm:mb-6 max-w-3xl mx-auto leading-relaxed">
            Show Your Art!
          </p>

          {/* Description */}
          <p className="text-base md:text-lg text-white/60 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed">
            Explore our collection of captivating stories, stunning artwork, and immersive narratives. 
            Dive into worlds of adventure, mystery, and imagination.
          </p>

          {/* CTA Button */}
          <button
            onClick={() => navigate('/comics')}
            className="group relative inline-flex items-center gap-3 px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-red-500 to-purple-500 text-white font-bold text-lg sm:text-xl rounded-xl hover:from-red-600 hover:to-purple-600 transition-all duration-300 shadow-lg shadow-red-500/20 hover:shadow-xl hover:shadow-red-500/30 hover:scale-105"
          >
            <span>Explore Comics</span>
            <ArrowRight size={24} className="group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-red-500/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-red-500 rounded-full"></div>
          </div>
        </div>
      </section>
      {/* Call to Action Section */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          {/* Background */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-purple-500/10"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.1),transparent_70%)]"></div>
          </div>

          {/* Content */}
          <div className="relative z-10 text-center bg-black/60 backdrop-blur-sm border border-red-500/20 rounded-3xl p-12 md:p-16">
            <h2 className="text-3xl md:text-5xl font-black mb-6 bg-gradient-to-r from-red-500 to-purple-500 bg-clip-text text-transparent">
              Ready to Start Reading?
            </h2>
            <p className="text-lg md:text-xl text-white/70 mb-8 max-w-2xl mx-auto">
              Browse our collection and find your next favorite comic adventure
            </p>
            <button
              onClick={() => navigate('/comics')}
              className="group inline-flex items-center gap-3 px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-red-500 to-purple-500 text-white font-bold text-lg sm:text-xl rounded-xl hover:from-red-600 hover:to-purple-600 transition-all duration-300 shadow-lg shadow-red-500/20 hover:shadow-xl hover:shadow-red-500/30 hover:scale-105"
            >
              <span>View All Comics</span>
              <ArrowRight size={24} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 px-6 border-t border-red-500/20">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} 4FingerStudios. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Welcome;
